import React from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stack,
  Avatar,
  useTheme,
  Paper,
  LinearProgress,
  Tooltip,
  Fade,
  Backdrop,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "react-query";
import { QK } from "../../../../../base/qk";
import { getMedication } from "../../../../../api/services/medication";
import dayjs from "dayjs";
import {
  AccessTime,
  Notes,
  CalendarMonth,
  MedicationLiquid,
  Layers,
  Inventory,
  Route,
  FactCheck,
  DoneAll,
} from "@mui/icons-material";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 860,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: "0px 24px 48px rgba(0, 0, 0, 0.12)",
  p: 3,
  outline: "none",
};

const StockMonitor = ({ initial, remaining, label = "Stock Levels" }) => {
  const theme = useTheme();
  const safeInitial = Math.max(Number(initial) || 0, Number(remaining) || 0);
  const percentage = safeInitial > 0 ? (Number(remaining) / safeInitial) * 100 : 0;
  const finalPercentage = Math.min(percentage, 100);

  const getStatusColor = () => {
    if (remaining <= 5) return theme.palette.error.main;
    if (remaining <= 10) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="textSecondary"
          sx={{ fontSize: "0.7rem", letterSpacing: 0.5 }}
        >
          {label.toUpperCase()}
        </Typography>
        <Typography variant="caption" fontWeight={700} color={getStatusColor()}>
          {remaining}{" "}
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            fontWeight={500}
          >
            / {safeInitial} Units
          </Typography>
        </Typography>
      </Stack>
      <Tooltip title={`${Math.round(finalPercentage)}% Available`} arrow>
        <LinearProgress
          variant="determinate"
          value={finalPercentage}
          sx={{
            height: 6,
            borderRadius: 4,
            bgcolor: "rgba(0,0,0,0.05)",
            "& .MuiLinearProgress-bar": {
              bgcolor: getStatusColor(),
              borderRadius: 4,
            },
          }}
        />
      </Tooltip>
    </Box>
  );
};

const DetailSection = ({ title, icon, color = "primary", children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
        <Avatar
          sx={{
            bgcolor: theme.palette[color].light,
            width: 28,
            height: 28,
            color: theme.palette[color].main,
          }}
        >
          <Box sx={{ display: "flex", "& svg": { fontSize: 16 } }}>{icon}</Box>
        </Avatar>
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: "0.85rem" }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
};

const MedicationDetailsModal = ({ open, handleClose, medId }) => {
  const theme = useTheme();
  const { data: med, isLoading } = useQuery(
    [QK.medicationDetails, medId],
    () => getMedication(medId),
    { enabled: !!medId }
  );

  if (isLoading || !med) return null;

  const isBlister = med.medicine_type === "Blister Pack";
  const isWeekly = med.frequency?.toLowerCase() === "weekly";
  const expiryDate = med.expiry_date ? dayjs(med.expiry_date) : null;
  const isExpired = expiryDate ? expiryDate.isBefore(dayjs()) : false;

  const numberOfCards = Number(med.number_of_cards || 0);
  const slotsPerCard = Number(med.slots_per_card || 0);
  const looseSlots = Number(med.loose_slots || 0);
  const totalSlotsRemaining = Number(med.total_slots_remaining || 0);
  const blisterInitialCapacity = numberOfCards * slotsPerCard + looseSlots;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: { backdropFilter: "blur(4px)", bgcolor: "rgba(0,0,0,0.4)" },
        },
      }}
    >
      <Fade in={open}>
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.75}>
              <Avatar
                sx={{
                  bgcolor: isBlister ? "warning.light" : "info.light",
                  color: isBlister ? "warning.main" : "info.main",
                  width: 48,
                  height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                }}
              >
                {isBlister ? (
                  <Layers sx={{ fontSize: 24 }} />
                ) : (
                  <MedicationLiquid sx={{ fontSize: 24 }} />
                )}
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ fontSize: "1.05rem", lineHeight: 1.2 }}
                >
                  {med.title}
                </Typography>
                <Stack direction="row" spacing={0.75} mt={0.5} flexWrap="wrap">
                  <Chip
                    label={med.medicine_type}
                    size="small"
                    color="info"
                    sx={{
                      fontWeight: 600,
                      borderRadius: 1,
                      height: 20,
                      fontSize: "0.65rem",
                    }}
                  />
                  <Chip
                    label={isExpired ? "EXPIRED" : "ACTIVE"}
                    size="small"
                    color={isExpired ? "error" : "success"}
                    sx={{
                      fontWeight: 700,
                      borderRadius: 1,
                      height: 20,
                      fontSize: "0.65rem",
                    }}
                  />
                  {med.controlled_medication && (
                    <Chip
                      label="CD"
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{
                        fontWeight: 700,
                        borderRadius: 1,
                        height: 20,
                        fontSize: "0.65rem",
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ bgcolor: "rgba(0,0,0,0.03)" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ flex: "1 1 45%", minWidth: "260px" }}>
              <DetailSection
                title="Contents & Inventory"
                icon={<Inventory />}
                color="primary"
              >
                <Stack spacing={1.25}>
                  {!isBlister &&
                    med.individual_meds?.map((item, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "#F8FAFF",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="primary.main"
                          sx={{ fontSize: "0.82rem" }}
                        >
                          {item.name || med.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={500}
                          color="textSecondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          Strength: {item.dose}
                        </Typography>
                        <StockMonitor
                          initial={item.initial_stock_qty}
                          remaining={item.remaining_stock}
                        />
                      </Paper>
                    ))}

                  {isBlister && (
                    <Stack spacing={1.25}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          bgcolor: "#FFFBF2",
                          border: "1px solid",
                          borderColor: "warning.light",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="warning.dark"
                          fontWeight={700}
                          sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                        >
                          BLISTER CONFIGURATION
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ mb: 0.5, fontSize: "0.82rem" }}
                        >
                          {numberOfCards} Cards × {slotsPerCard} Slots
                          {looseSlots > 0 && ` + ${looseSlots} Loose`}
                        </Typography>
                        <StockMonitor
                          initial={blisterInitialCapacity}
                          remaining={totalSlotsRemaining}
                          label="Total Slots Tracking"
                        />
                      </Paper>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          borderRadius: 2.5,
                          border: "1px solid #E0E0E0",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          fontWeight={700}
                          sx={{
                            mb: 0.75,
                            display: "block",
                            fontSize: "0.65rem",
                            letterSpacing: 0.5,
                          }}
                        >
                          MEDICINES IN THIS PACK
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                          {med.medicine_lists?.map((m) => (
                            <Chip
                              key={m.id}
                              icon={<DoneAll sx={{ fontSize: 14 }} />}
                              label={m.name}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                height: 22,
                                fontSize: "0.7rem",
                              }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Stack>
                  )}
                </Stack>
              </DetailSection>

              <DetailSection
                title="Administration Guide"
                icon={<Route />}
                color="success"
              >
                <Paper
                  variant="outlined"
                  sx={{ p: 1.75, borderRadius: 2.5, bgcolor: "#F9FAFB" }}
                >
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                      >
                        ROUTE
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontSize: "0.82rem" }}
                      >
                        {med.route_administration}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                      >
                        ASSISTANCE LEVEL
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontSize: "0.82rem" }}
                      >
                        {med.how_assisted}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </DetailSection>
            </Box>

            <Box sx={{ flex: "1 1 45%", minWidth: "260px" }}>
              <DetailSection
                title="Schedule & Frequency"
                icon={<CalendarMonth />}
                color="secondary"
              >
                <Paper
                  variant="outlined"
                  sx={{ p: 1.75, borderRadius: 2.5, bgcolor: "#FDFEFF" }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <Box sx={{ flex: "1 1 40%", minWidth: "100px" }}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                      >
                        FREQUENCY
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="secondary.main"
                        sx={{ fontSize: "0.82rem" }}
                      >
                        {med.frequency}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: "1 1 40%", minWidth: "100px" }}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                      >
                        INTERVAL
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontSize: "0.82rem" }}
                      >
                        Every {med.every} Week(s)
                      </Typography>
                    </Box>

                    {isWeekly && med.weekdays && (
                      <Box sx={{ width: "100%" }}>
                        <Divider sx={{ my: 0.75, borderStyle: "dotted" }} />
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          fontWeight={700}
                          sx={{
                            mb: 0.75,
                            display: "block",
                            fontSize: "0.65rem",
                            letterSpacing: 0.5,
                          }}
                        >
                          SCHEDULED DAYS
                        </Typography>
                        <Stack direction="row" spacing={0.75}>
                          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                            (day) => {
                              const isActive = med.weekdays.includes(day);
                              return (
                                <Avatar
                                  key={day}
                                  sx={{
                                    width: 26,
                                    height: 26,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    bgcolor: isActive
                                      ? "secondary.main"
                                      : "rgba(0,0,0,0.04)",
                                    color: isActive ? "#fff" : "text.disabled",
                                    border: isActive ? "none" : "1px solid #EEE",
                                  }}
                                >
                                  {day.substring(0, 1)}
                                </Avatar>
                              );
                            }
                          )}
                        </Stack>
                      </Box>
                    )}

                    <Box sx={{ flex: "1 1 40%", minWidth: "100px", mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                      >
                        START DATE
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontSize: "0.82rem" }}
                      >
                        {dayjs(med.start_date).format("DD MMM YYYY")}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: "1 1 40%", minWidth: "100px", mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                      >
                        EXPIRY
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={isExpired ? "error.main" : "success.main"}
                        sx={{ fontSize: "0.82rem" }}
                      >
                        {expiryDate
                          ? expiryDate.format("DD MMM YYYY")
                          : "No Expiry"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </DetailSection>

              <DetailSection
                title="Associated Visit Mapping"
                icon={<FactCheck />}
                color="info"
              >
                <Stack spacing={1}>
                  {med.time_data?.map((t) => (
                    <Box
                      key={t.id}
                      sx={{
                        p: 1.25,
                        borderRadius: 2.5,
                        border: "1px solid #F0F4F8",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "#FFF",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box
                          sx={{
                            p: 0.6,
                            bgcolor: "info.light",
                            borderRadius: 1.5,
                            display: "flex",
                          }}
                        >
                          <AccessTime sx={{ fontSize: 14, color: "info.main" }} />
                        </Box>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ fontSize: "0.82rem" }}
                          >
                            {dayjs(`2000-01-01T${t.time}`).format("h:mm A")}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            Scheduled Time
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        label={
                          med.care_plan?.find((c) => c.id === t.careplan)?.name ||
                          "Unmapped Visit"
                        }
                        size="small"
                        color="info"
                        sx={{
                          fontWeight: 600,
                          bgcolor: "#E8F4FD",
                          height: 22,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                  ))}
                  {(!med.time_data || med.time_data.length === 0) && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{
                        fontStyle: "italic",
                        textAlign: "center",
                        fontSize: "0.7rem",
                      }}
                    >
                      No specific visit times mapped
                    </Typography>
                  )}
                </Stack>
              </DetailSection>
            </Box>

            <Box sx={{ width: "100%" }}>
              <DetailSection
                title="Clinical Notes & Instructions"
                icon={<Notes />}
                color="warning"
              >
                <Paper
                  sx={{
                    p: 1.75,
                    borderRadius: 2.5,
                    bgcolor: "#FFFDF0",
                    border: "1px solid",
                    borderColor: "warning.light",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      p: 1,
                      opacity: 0.08,
                    }}
                  >
                    <Notes sx={{ fontSize: 60 }} />
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ flex: "1 1 40%", minWidth: "200px" }}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                      >
                        REASON FOR MEDICATION
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, fontWeight: 500, fontSize: "0.82rem" }}
                      >
                        {med.reason_for_medication || "Not provided"}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: "1 1 40%", minWidth: "200px" }}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        fontWeight={700}
                        sx={{ fontSize: "0.65rem", letterSpacing: 0.5 }}
                      >
                        SPECIAL INSTRUCTIONS
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          fontWeight: 500,
                          color: "warning.dark",
                          fontSize: "0.82rem",
                        }}
                      >
                        {med.notes || "No special instructions"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </DetailSection>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default MedicationDetailsModal;